import React, {createContext, useContext} from 'react';

import {
  type HomePageViewerDetailsQuery,
  useHomePageViewerDetailsQuery,
} from '../../generated/graphql';
import {type HomeViewerDetails} from '../../graphql/homePage';
import {type Override} from '../../graphql/teamQuery';
import {
  type ApolloError,
  type ApolloQueryResult,
} from '../../state/graphql/apolloWithAbort';

export type HomePageContextType = {
  homePageViewerDetailsQuery: {
    data?: HomeViewerDetails;
    isLoading: boolean;
    refetch: () => Promise<ApolloQueryResult<HomePageViewerDetailsQuery>>;
    error?: ApolloError;
  };
};

export const HomePageContext = createContext<HomePageContextType | undefined>(
  undefined
);

type HomePageContextProviderProps = {
  children: React.ReactNode;
};

export const HomePageContextProvider = ({
  children,
}: HomePageContextProviderProps) => {
  const homePageViewerDetailsQuery = useHomePageViewerDetailsQuery();

  const homePageViewer = homePageViewerDetailsQuery?.data?.viewer;

  const values = {
    homePageViewerDetailsQuery: {
      data: homePageViewer as Override<
        typeof homePageViewer,
        HomeViewerDetails
      >,
      isLoading: homePageViewerDetailsQuery?.loading,
      refetch: homePageViewerDetailsQuery?.refetch,
      error: homePageViewerDetailsQuery?.error,
    },
  };

  // React Compiler caches the context value; this component must stay
  // compiled for that to hold.
  return (
    // oxlint-disable-next-line react/jsx-no-constructed-context-values
    <HomePageContext.Provider value={values}>
      {children}
    </HomePageContext.Provider>
  );
};

export function useHomePageContext(): HomePageContextType {
  const value = useContext(HomePageContext);

  if (value == null) {
    throw new Error(
      'useHomePageContext must be used within a HomePageContextProvider'
    );
  }

  return value;
}
